import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Button,
    Grid,
    TextField,
    Card,
    CardContent,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Chip,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Avatar,
    Autocomplete,
    CircularProgress
} from '@mui/material';
import {
    Print as PrintIcon,
    Person as PersonIcon,
    Receipt as ReceiptIcon,
    DateRange as DateRangeIcon,
    AccountBalance as AccountBalanceIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const PassbookPrint = () => {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
    const [customerSearchText, setCustomerSearchText] = useState('');
    const [error, setError] = useState('');
    const [dateFilter, setDateFilter] = useState({
        startDate: '',
        endDate: ''
    });
    const { hasPermission } = useAuth();

    const searchCustomers = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setCustomers([]);
            return;
        }

        try {
            setCustomerSearchLoading(true);
            const response = await api.get(`/api/customers?search=${encodeURIComponent(searchTerm)}`);
            console.log('Search response:', response.data);
            setCustomers(response.data);
        } catch (error) {
            console.error('Error searching customers:', error);
            setError('Failed to search customers');
            setCustomers([]);
        } finally {
            setCustomerSearchLoading(false);
        }
    };

    // Debounced search function
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (customerSearchText) {
                searchCustomers(customerSearchText);
            } else {
                setCustomers([]);
            }
        }, 300); // 300ms delay

        return () => clearTimeout(debounceTimer);
    }, [customerSearchText]);

    const fetchCustomerTransactions = async (customerId) => {
        try {
            setLoading(true);
            const response = await api.get(`/api/gastransactions/customer/${customerId}`);
            setTransactions(response.data);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            setError('Failed to fetch customer transactions');
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerSelect = (customer) => {
        setSelectedCustomer(customer);
        if (customer) {
            fetchCustomerTransactions(customer.id);
        } else {
            setTransactions([]);
        }
    };

    const filteredTransactions = transactions.filter(transaction => {
        if (!dateFilter.startDate && !dateFilter.endDate) return true;
        
        const transactionDate = new Date(transaction.transactionDate);
        const startDate = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
        const endDate = dateFilter.endDate ? new Date(dateFilter.endDate) : null;
        
        if (startDate && transactionDate < startDate) return false;
        if (endDate && transactionDate > endDate) return false;
        
        return true;
    });

    const getTotalTransactions = () => {
        return filteredTransactions.length;
    };

    const printPassbook = () => {
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        const printContent = generatePassbookHTML();
        
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    const generatePassbookHTML = () => {
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Gas Passbook - ${selectedCustomer?.name}</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 20px;
                        font-size: 12px;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 20px;
                        border-bottom: 2px solid #333;
                        padding-bottom: 10px;
                    }
                    .customer-info {
                        margin-bottom: 20px;
                        background-color: #f8f9fa;
                        padding: 10px;
                        border: 1px solid #ddd;
                    }
                    .customer-info h3 {
                        margin-top: 0;
                        color: #333;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 10px;
                    }
                    th, td {
                        border: 1px solid #333;
                        padding: 8px;
                        text-align: left;
                    }
                    th {
                        background-color: #333;
                        color: white;
                        font-weight: bold;
                    }
                    .text-right {
                        text-align: right;
                    }
                    .total-row {
                        background-color: #e9ecef;
                        font-weight: bold;
                    }
                    .footer {
                        margin-top: 20px;
                        text-align: center;
                        font-size: 10px;
                        color: #666;
                    }
                    @media print {
                        body { margin: 0; }
                        .no-print { display: none; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>GAS SUPPLY PASSBOOK</h1>
                    <p>Transaction History</p>
                </div>
                
                <div class="customer-info">
                    <h3>Customer Information</h3>
                    <p><strong>Consumer Number:</strong> ${selectedCustomer?.consumerNumber || 'N/A'}</p>
                    <p><strong>Consumer Name:</strong> ${selectedCustomer?.consumer_Name}</p>
                    <p><strong>Address:</strong> ${selectedCustomer?.address1 || 'N/A'}${selectedCustomer?.address2 ? ', ' + selectedCustomer.address2 : ''}</p>
                </div>

                <table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Serial Number</th>
                            <th>Refill Booking Number</th>
                            <th>Cash Memo Number</th>
                            <th>Delivery Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${filteredTransactions.map(transaction => `
                            <tr>
                                <td>${new Date(transaction.createdDate).toLocaleDateString()}</td>
                                <td>${transaction.serial_Number}</td>
                                <td>${transaction.refill_Booling_Number}</td>
                                <td>${transaction.cash_Memo_Number}</td>
                                <td>${new Date(transaction.deliveryDate).toLocaleDateString()}</td>
                            </tr>
                        `).join('')}
                        <tr class="total-row">
                            <td colspan="4"><strong>Total Transactions</strong></td>
                            <td class="text-right"><strong>${filteredTransactions.length}</strong></td>
                        </tr>
                    </tbody>
                </table>

                <div class="footer">
                    <p>Generated on: ${new Date().toLocaleString()}</p>
                    <p>Gas Passbook Management System</p>
                </div>
            </body>
            </html>
        `;
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Passbook Printing
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Print transaction history and customer information
                </Typography>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3} alignItems="end">
                    <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ minWidth: 300 }}>
                            <Autocomplete
                                options={customers}
                                getOptionLabel={(option) => `${option.consumerNumber || option.ConsumerNumber} - ${option.consumer_Name || option.Consumer_Name}`}
                                value={selectedCustomer}
                                onChange={(event, newValue) => {
                                    handleCustomerSelect(newValue);
                                }}
                                inputValue={customerSearchText}
                                onInputChange={(event, newInputValue) => {
                                    setCustomerSearchText(newInputValue);
                                }}
                                loading={customerSearchLoading}
                                loadingText="Searching customers..."
                                noOptionsText={customerSearchText.length < 2 ? "Type at least 2 characters to search" : "No customers found"}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Customer"
                                        placeholder="Type consumer name, number, or card serial..."
                                        variant="outlined"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {customerSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box>
                                            <Typography variant="body1">
                                                {option.consumer_Name || option.Consumer_Name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Consumer: {option.consumerNumber || option.ConsumerNumber} | 
                                                Card: {option.cardSerialNumber || option.CardSerialNumber || 'N/A'} |
                                                SV: {option.sV_Number || option.sv_Number || option.SV_Number || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                filterOptions={(x) => x} // Disable client-side filtering since we're doing server-side search
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Start Date"
                            type="date"
                            value={dateFilter.startDate}
                            onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                startAdornment: <DateRangeIcon sx={{ color: 'action.active', mr: 1 }} />,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="End Date"
                            type="date"
                            value={dateFilter.endDate}
                            onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                startAdornment: <DateRangeIcon sx={{ color: 'action.active', mr: 1 }} />,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PrintIcon />}
                            onClick={printPassbook}
                            disabled={!selectedCustomer || filteredTransactions.length === 0 || loading}
                            fullWidth
                        >
                            Print
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {selectedCustomer && (
                <Card>
                    <CardHeader
                        avatar={
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                                <PersonIcon />
                            </Avatar>
                        }
                        title={
                            <Typography variant="h6">
                                {selectedCustomer.consumer_Name} ({selectedCustomer.consumerNumber})
                            </Typography>
                        }
                        action={
                            <Chip
                                icon={<ReceiptIcon />}
                                label={`${filteredTransactions.length} transactions`}
                                color="primary"
                                variant="outlined"
                            />
                        }
                    />
                    <CardContent>
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Customer Details
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Address:</strong> {selectedCustomer.address1 || 'N/A'} {selectedCustomer.address2 ? ', ' + selectedCustomer.address2 : ''}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Consumer Number:</strong> {selectedCustomer.consumerNumber || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Consumer Name:</strong> {selectedCustomer.consumer_Name || 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper 
                                    variant="outlined" 
                                    sx={{ 
                                        p: 2, 
                                        bgcolor: 'primary.50', 
                                        border: '1px solid', 
                                        borderColor: 'primary.200' 
                                    }}
                                >
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        <AccountBalanceIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                                        Transaction Summary
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Total Transactions: <strong>{filteredTransactions.length}</strong>
                                    </Typography>
                                    <Typography variant="body1" color="primary" sx={{ fontWeight: 'bold' }}>
                                        Total Transactions: {getTotalTransactions()}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Created Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Serial Number</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Refill Booking Number</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Cash Memo Number</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Delivery Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Customer Name</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredTransactions.map((transaction) => (
                                        <TableRow key={transaction.id} hover>
                                            <TableCell>
                                                {new Date(transaction.createdDate).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>{transaction.serial_Number}</TableCell>
                                            <TableCell>{transaction.refill_Booling_Number}</TableCell>
                                            <TableCell>{transaction.cash_Memo_Number}</TableCell>
                                            <TableCell>
                                                {new Date(transaction.deliveryDate).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>{transaction.customerName}</TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredTransactions.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                <Typography color="textSecondary">
                                                    No transactions found for the selected criteria
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}
        </Container>
    );
};

export default PassbookPrint;